import { type ElementType, useEffect, useLayoutEffect, useMemo } from 'react'
import { clsx } from 'clsx'
import Icon, { type IconTypes } from '@components/utils/Icon'
import { SpriteUrlProvider } from '@components/utils/spriteUrl'
import { scopedTranslation } from '@frontend/utils/I18n'
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from '@components/atoms/Sidebar'
import { GroupSwitcher } from '@molecules/GroupSwitcher'
import { type NavItem, type NavTree } from './navTree'
import { accessibleLabel } from './sidebarItem'
import { currentLocation, isItemActive, type Location } from './activeMatch'

const t = scopedTranslation('components.sidebar')

const ICON_SIZE = 20

export function applySidebarSurface({
  sidebarColor,
  foregroundColor,
}: Pick<NavTree, 'sidebarColor' | 'foregroundColor'>): void {
  const sidebar = sidebarColor ?? '#000000'
  const foreground = foregroundColor ?? '#ffffff'
  const root = document.documentElement.style
  root.setProperty('--sidebar', sidebar)
  root.setProperty('--sidebar-foreground', foreground)
  root.setProperty('--sidebar-primary', 'var(--sidebar-foreground)')
  root.setProperty('--sidebar-primary-foreground', 'var(--sidebar)')
  root.setProperty('--sidebar-accent', 'color-mix(in srgb, var(--sidebar-foreground) 16%, transparent)')
  root.setProperty('--sidebar-accent-foreground', 'var(--sidebar-foreground)')
  root.setProperty('--sidebar-border', 'color-mix(in srgb, var(--sidebar-foreground) 24%, transparent)')
  root.setProperty('--sidebar-ring', 'var(--sidebar-foreground)')
}

type SidebarProps = {
  tree: NavTree
  defaultOpen?: boolean
  spriteUrl?: string
  /**
   * Current location for active-item matching. Inertia pages pass this from
   * `usePage().url` so highlighting tracks client-side navigation; the v1
   * react-rails mount omits it and falls back to `window.location`.
   */
  location?: Location
  linkComponent?: ElementType<{ href: string }>
}

type ItemProps = {
  item: NavItem
  location: Location
  collapsed: boolean
  onNavigate: () => void
  linkComponent: ElementType<{ href: string }>
}

function linkAttrs(item: NavItem) {
  return {
    href: item.href,
    'aria-label': accessibleLabel(item),
    ...(item.method ? { 'data-method': item.method, rel: 'nofollow' } : {}),
  }
}

function NavBadge({ item }: { item: NavItem }) {
  if (!item.badge || item.badge.count <= 0) return null
  return (
    <span
      aria-hidden="true"
      className={clsx(
        'tw:ml-auto tw:inline-flex tw:min-w-5 tw:items-center tw:justify-center tw:rounded-full tw:bg-red-600 tw:px-1.5 tw:text-xs tw:font-semibold tw:text-white',
        'tw:group-data-[collapsible=icon]:absolute tw:group-data-[collapsible=icon]:right-1 tw:group-data-[collapsible=icon]:top-0 tw:group-data-[collapsible=icon]:ml-0 tw:group-data-[collapsible=icon]:px-1'
      )}
    >
      {item.badge.count}
    </span>
  )
}

function LeafItem({ item, location, collapsed, onNavigate, linkComponent }: ItemProps) {
  const active = isItemActive(item, location)
  const LinkComponent = item.method ? 'a' : linkComponent
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={collapsed ? item.label : undefined}>
        <LinkComponent {...linkAttrs(item)} aria-current={active ? 'page' : undefined} onClick={onNavigate}>
          {item.icon && <Icon type={item.icon as IconTypes} width={ICON_SIZE} height={ICON_SIZE} />}
          <span className="tw:flex-1 tw:truncate tw:group-data-[collapsible=icon]:hidden">{item.label}</span>
          <NavBadge item={item} />
        </LinkComponent>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function SidebarChrome({
  tree,
  location: locationProp,
  linkComponent: LinkComponent,
}: {
  tree: NavTree
  location?: Location
  linkComponent: ElementType<{ href: string }>
}) {
  const fallbackLocation = useMemo(currentLocation, [])
  const location = locationProp ?? fallbackLocation
  const { state, isMobile, openMobile, setOpenMobile, toggleSidebar } = useSidebar()
  const collapsed = state === 'collapsed'

  const dismiss = () => {
    setOpenMobile(false)
  }

  useEffect(() => {
    const width = isMobile ? '0px' : collapsed ? '5rem' : '16.25rem'
    document.documentElement.style.setProperty('--sb-content-offset', width)
  }, [collapsed, isMobile])

  return (
    <>
      {!openMobile && (
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={t('open_menu')}
          className="tw:fixed tw:right-4 tw:top-[calc(1rem+var(--sb-banner-height,0px))] tw:z-60 tw:flex tw:h-11 tw:w-11 tw:items-center tw:justify-center tw:rounded-lg tw:bg-sidebar tw:text-sidebar-foreground tw:shadow-lg tw:lg:hidden"
        >
          <Icon type="menu-01" width={24} height={24} />
        </button>
      )}
      <SidebarRoot collapsible="icon">
        <div className="tw:flex tw:h-full tw:min-h-svh tw:w-full tw:flex-col">
          {isMobile && (
            <div className="tw:flex tw:justify-end tw:px-3 tw:pt-3">
              <button
                type="button"
                onClick={dismiss}
                aria-label={t('close_menu')}
                className="tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-md tw:text-sidebar-foreground tw:hover:bg-sidebar-accent"
              >
                <Icon type="x-close" width={24} height={24} />
              </button>
            </div>
          )}
          <SidebarHeader>
            <LinkComponent
              href={tree.homeUrl}
              aria-label={t('home')}
              className="tw:flex tw:w-full tw:items-center tw:justify-center tw:py-1"
            >
              {tree.logoUrl ? (
                <img
                  src={tree.logoUrl}
                  alt=""
                  className="tw:h-auto tw:max-h-16 tw:w-full tw:object-contain tw:group-data-[collapsible=icon]:h-6 tw:group-data-[collapsible=icon]:w-6"
                />
              ) : (
                <Icon type="community" width={32} height={32} />
              )}
            </LinkComponent>
            {tree.scope && <GroupSwitcher {...tree.scope} linkComponent={LinkComponent} />}
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarMenu>
                {tree.sections.map((section) => (
                  <LeafItem
                    key={section.key}
                    item={section}
                    location={location}
                    collapsed={collapsed}
                    onNavigate={dismiss}
                    linkComponent={LinkComponent}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="tw:border-t tw:border-sidebar-border">
            <SidebarMenu>
              {tree.footer.map((item) => (
                <LeafItem
                  key={item.key}
                  item={item}
                  location={location}
                  collapsed={collapsed}
                  onNavigate={dismiss}
                  linkComponent={LinkComponent}
                />
              ))}
            </SidebarMenu>
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={collapsed ? t('expand') : t('collapse')}
              aria-pressed={collapsed}
              className="tw:mt-1 tw:hidden tw:w-full tw:items-center tw:justify-center tw:rounded-md tw:py-2 tw:text-sidebar-foreground/70 tw:hover:bg-sidebar-accent tw:hover:text-sidebar-accent-foreground tw:lg:flex"
            >
              <Icon type={collapsed ? 'chevron-right' : 'chevron-left'} width={ICON_SIZE} height={ICON_SIZE} />
            </button>
          </SidebarFooter>
        </div>
      </SidebarRoot>
    </>
  )
}

export function Sidebar({ tree, defaultOpen, spriteUrl, location, linkComponent = 'a' }: SidebarProps) {
  useLayoutEffect(() => {
    applySidebarSurface(tree)
  }, [tree.sidebarColor, tree.foregroundColor])

  return (
    <SpriteUrlProvider url={spriteUrl}>
      <SidebarProvider defaultOpen={defaultOpen} className="tw:contents">
        <SidebarChrome tree={tree} location={location} linkComponent={linkComponent} />
      </SidebarProvider>
    </SpriteUrlProvider>
  )
}
